import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { IoStorefrontOutline, IoLanguageOutline } from "react-icons/io5";
import { MdQuestionAnswer, MdOutlineShoppingCart } from 'react-icons/md';
import { FaUser, FaComments } from 'react-icons/fa';
import { GiPaintBrush } from 'react-icons/gi';
import useLogout from '../Hooks/LogoutHook/useLogout';
import logo1 from '../Assests/logo1.png';

const Sidebar = ({ onSelect }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState(null);
  const logout = useLogout();

  const handleLogout = () => {
    setModalOpen(true);
  };

  const confirmLogout = async () => {
    setIsLoggingOut(true);
    setLogoutError(null);
    try {
      await logout();
      // Navigation will be handled by logout hook
    } catch (error) {
      setLogoutError('Logout failed. Please try again.');
      setIsLoggingOut(false);
      console.error('Logout error:', error);
    }
  };

  const handleNavClick = (item) => {
    setSelectedItem(item);
    onSelect(item);
  };

  const handleExternalNavClick = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <SidebarContainer>
      <LogoContainer>
        <LogoCircle>
          <LogoImage src={logo1} alt="Logo" />
        </LogoCircle>
        <LogoText>eBaybayMo</LogoText>
      </LogoContainer>
      
      <NavItems>
        <NavItem 
          onClick={() => handleNavClick('Home')}
          $isSelected={selectedItem === 'Home'}
        >
          <IoStorefrontOutline className="icon" />
          Shop
        </NavItem>

        <NavItem onClick={() => handleExternalNavClick('https://baiguhit.bisubilar.org/')}>
          <IoLanguageOutline className="icon" /> Baiguhit
        </NavItem>

        <NavItem onClick={() => handleExternalNavClick('https://badlit.bisubilar.org/')}>
          <IoLanguageOutline className="icon" /> Badlit
        </NavItem>
       
        <NavItem
          onClick={() => handleNavClick('ClientChats')}
          $isSelected={selectedItem === 'ClientChats'}
        >
          <FaComments className="icon" /> Messages
        </NavItem>
        <NavItem 
          onClick={() => handleNavClick('Order')}
          $isSelected={selectedItem === 'Order'}
        >
          <MdOutlineShoppingCart className="icon" /> Order List
        </NavItem>
        <NavItem 
          onClick={() => handleNavClick('Transliterate')}
          $isSelected={selectedItem === 'Transliterate'}
        >
          <IoLanguageOutline className="icon" /> Transliterate
        </NavItem>
        <NavItem 
          onClick={() => handleNavClick('Survey')}
          $isSelected={selectedItem === 'Survey'}
        >
          <MdQuestionAnswer className="icon" /> Survey
        </NavItem>

        <NavItem 
          onClick={() => handleNavClick('HennaBooking')}
          $isSelected={selectedItem === 'HennaBooking'}
        >
          <GiPaintBrush className="icon" /> Henna Booking
        </NavItem>

        <NavItem 
          onClick={() => handleNavClick('ProfileV2')}
          $isSelected={selectedItem === 'ProfileV2'}
        >
          <FaUser className="icon" /> Profile
        </NavItem>
      </NavItems>

      <LogoutButton onClick={handleLogout}>
        Logout
      </LogoutButton>

      {isModalOpen && ReactDOM.createPortal(
        <ConfirmationModal 
          isOpen={isModalOpen} 
          onRequestClose={() => {
            if (!isLoggingOut) {
              setModalOpen(false);
              setLogoutError(null);
            }
          }} 
          onConfirm={confirmLogout}
          message="Are you sure you want to logout?"
          isLoading={isLoggingOut}
          error={logoutError}
        />,
        document.body
      )}
    </SidebarContainer>
  );
};

const ConfirmationModal = ({ isOpen, onRequestClose, onConfirm, message, isLoading = false, error = null }) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalMessage>{message}</ModalMessage>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <ModalButtons>
          <ModalButton onClick={onConfirm} primary disabled={isLoading}>
            {isLoading ? 'Logging out...' : 'Yes'}
          </ModalButton>
          <ModalButton onClick={onRequestClose} disabled={isLoading}>No</ModalButton>
        </ModalButtons>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default Sidebar;

const SidebarContainer = styled.div`
  height: 100vh;
  width: 200px;
  flex-shrink: 0;
  box-sizing: border-box;
  position: sticky;
  top: 0;
  align-self: flex-start;
  overflow-y: auto;
  background: linear-gradient(90deg, rgb(170, 39, 13), rgb(231, 145, 91));
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  justify-content: space-between;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: center;
  border-bottom: 4px solid #ffffff;
  padding-bottom: 15px;
`;

const LogoCircle = styled.div`
  border: 2px solid #ffffff;
  border-radius: 100%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LogoImage = styled.img`
  width: 35px;
  height: 35px;
  border-radius: 100%;
  object-fit: contain;
`;

const LogoText = styled.h1`
  padding -right: 10px;
  font-family: 'Poppins';
  font-size: 1.5rem;
  color: #ffffff;
  font-weight: 700;
  text-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
`;

const NavItems = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  flex-grow: 1;
  margin-top: 50px;
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: calc(100% - 10px);
  padding: 12px 15px 12px 25px;
  margin-left: 0;
  margin-right: auto;
  margin-bottom: 10px;
  font-family: 'Poppins';
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
  border-top-left-radius: 20px;
  border-bottom-left-radius: 20px;
  background-color: ${props => (props.$isSelected ? '#ffffff' : 'transparent')};
  color: ${props => (props.$isSelected ? '#A52A2A' : '#ffffff')};
  transition: background-color 0.3s, color 0.3s, width 0.3s;

  &:hover {
    background-color: #ffffff;
    color: #A52A2A;
  }

  .icon {
    margin-right: 10px;
    font-size: 1.5rem;
  }
`;

const LogoutButton = styled.button`
  position: relative;
  overflow: hidden;
  width: 100%;
  padding: 10px;
  font-family: 'Poppins';
  font-size: 1rem;
  color: #ffffff;
  background: linear-gradient(90deg, rgb(170, 39, 13), rgb(231, 145, 91));
  font-weight: bold;
  cursor: pointer;
  margin-bottom: 30px;
  border: 2px solid #ffffff;
  border-radius: 8px;

  &:hover {
    background-color: #ffffff;
    color: #A52A2A;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -75%;
    width: 50%;
    height: 100%;
    background: linear-gradient(
      120deg,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0.6) 50%,
      rgba(255, 255, 255, 0.1) 100%
    );
    transform: skewX(-20deg);
    animation: shimmer 2s infinite;
  }

  @keyframes shimmer {
    0% {
      left: -75%;
    }
    100% {
      left: 125%;
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
`;

const ModalContainer = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalMessage = styled.p`
  font-family: 'Poppins';
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 25px;
  line-height: 1.5;
  font-weight: 500;
`;

const ErrorMessage = styled.p`
  font-family: 'Poppins';
  font-size: 0.95rem;
  color: #dc3545;
  margin-bottom: 15px;
  font-weight: 500;
  background-color: #f8d7da;
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #f5c6cb;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
`;

const ModalButton = styled.button`
  padding: 12px 30px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: 'Poppins';
  font-size: 1rem;
  font-weight: 600;
  background-color: ${props => (props.primary ? '#A52A2A' : '#6c757d')};
  color: white;
  transition: all 0.3s ease;
  min-width: 100px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: ${props => (props.primary ? '#8B1B1B' : '#5a6268')};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;